import React from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Handshake,
  Users2,
  ShieldCheck,
  Globe,
  ArrowRight,
  ChevronRight,
  Database,
  Briefcase,
  Zap,
  Cpu
} from "lucide-react";
import Navbar from "../Components/Navbar";
import Call from "../Components/Call";
import Footer from "../Components/Footer";
import ChatbotWidget from "./ChatComponent";
import "../Styling/Partner.css";

gsap.registerPlugin(ScrollTrigger);

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0,
      delayChildren: 0
    }
  }
};

const itemVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0 }
  }
};

export default function Partner() {
  const heroRef = React.useRef(null);

  React.useEffect(() => {
    const ctx = gsap.context(() => {
      // Floating Animation for Cards
      gsap.to(".main-card", {
        y: -15,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-1", {
        y: 12,
        x: 8,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-2", {
        y: -12,
        x: -8,
        duration: 3.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      // Ambient blobs movement
      gsap.to(".Partnership-blob", {
        x: "random(-100, 100)",
        y: "random(-100, 100)",
        duration: 10,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: 0.5
      });

      // Background mesh rotation
      gsap.to(".Partnership-mesh-overlay", {
        rotation: 360,
        duration: 60,
        repeat: -1,
        ease: "none"
      });

      ScrollTrigger.refresh();
    }, heroRef);

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      ctx.revert();
      clearTimeout(refreshTimer);
    };
  }, []);

  return (
    <div className="Partnership-page-wrapper">
      <Navbar />

      {/* Hero Section - The "Never Seen Before" Gradient */}
      <section className="Partnership-hero-section" ref={heroRef}>
        <div className="Partnership-mesh-container">
          <div className="Partnership-mesh-overlay"></div>
          <div className="Partnership-blob blob-purple"></div>
          <div className="Partnership-blob blob-blue"></div>
          <div className="Partnership-blob blob-cyan"></div>
        </div>

        <div className="container">
          <div className="Partnership-hero-layout">
            <motion.div
              className="Partnership-hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants} className="Partnership-badge-premium">
                <Handshake size={16} /> Ecosystem Growth
              </motion.div>
              <motion.h1 variants={itemVariants} className="Partnership-hero-title-main">
                Scale Your Ecosystem With <span className="magenta-gradient">DDW Intelligence</span>
              </motion.h1>
              <motion.p variants={itemVariants} className="Partnership-hero-desc-main">
                We partner with select agencies, enterprises, and strategic innovators to deploy our proprietary ecosystem. Whether through white label licensing or co-developed architecture, we build the technical foundation for your growth.
              </motion.p>
              <motion.div variants={itemVariants} className="Partnership-hero-ctas">
                <a href="https://calendly.com/digi-dreamworks/onboarding-call" className="Partnership-btn-primary">
                  Inquire for Partnership <ArrowRight size={20} />
                </a>
                <div className="Partnership-trust-label">
                  <ShieldCheck size={18} /> Production-Grade Alignment
                </div>
              </motion.div>
            </motion.div>

            <div className="Partnership-hero-visual">
              <div className="visual-card main-card">
                <Cpu size={40} />
                <span>AI Core</span>
              </div>
              <div className="visual-card secondary-card card-1">
                <Globe size={24} />
                <span>Global Scale</span>
              </div>
              <div className="visual-card secondary-card card-2">
                <Database size={24} />
                <span>Edge Data</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Models */}
      <section className="Partnership-models-section">
        <div className="container">
          <div className="Partnership-section-header">
            <span className="Partnership-section-tag">Integration Models</span>
            <h2 className="Partnership-section-title">How We Partner</h2>
          </div>

          <div className="Partnership-models-grid-v2">
            {[
              {
                icon: <Zap size={32} />,
                title: "White-Label Licensing",
                desc: "Deploy our entire terminal and automation ecosystem under your own brand identity.",
                features: ["Brand Reskinning", "Dedicated Clusters", "Client Monitoring Hub"]
              },
              {
                icon: <Users2 size={32} />,
                title: "Strategic Resale",
                desc: "Package our systems as part of your service offering to high-value enterprise clients.",
                features: ["Revenue Incentives", "Sales Enablement", "Partner Dashboard"]
              },
              {
                icon: <Database size={32} />,
                title: "Infrastructure Integration",
                desc: "Embed our logic layers into your existing software via secure API orchestration.",
                features: ["Modular Logic", "Scalable Endpoints", "Technical Consulting"]
              },
              {
                icon: <Briefcase size={32} />,
                title: "Enterprise Solutions",
                desc: "Co-develop vertical-specific platforms using our proprietary base modules.",
                features: ["Shared IP Access", "Custom Modules", "Vertical Dominance"]
              }
            ].map((model, i) => (
              <motion.div
                key={i}
                className="Partnership-model-card-v2"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="model-icon-box">{model.icon}</div>
                <h3 className="model-h3">{model.title}</h3>
                <p className="model-p">{model.desc}</p>
                <div className="model-features">
                  {model.features.map((f, j) => (
                    <div key={j} className="model-feature-item">
                      <ChevronRight size={14} /> {f}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements & Process */}
      <section className="Partnership-details-section">
        <div className="container">
          <div className="Partnership-details-box">
            <div className="details-header">
              <h2 className="details-title">Protocol & Requirements</h2>
              <p className="details-intro">We prioritize depth over volume. Every partnership undergoes a rigorous evaluation to ensure technical and operational alignment.</p>
            </div>

            <div className="details-grid">
              <div className="details-list-side">
                <h4 className="list-title">Ideal Partner Profile</h4>
                <ul className="details-list">
                  <li><div className="check-dot"></div> Established market presence in tech/logistics/HR.</li>
                  <li><div className="check-dot"></div> Internal technical capacity for tier-1 support.</li>
                  <li><div className="check-dot"></div> Clear growth roadmap for ecosystem adoption.</li>
                  <li><div className="check-dot"></div> Commitment to production-grade data standards.</li>
                </ul>
              </div>
              <div className="details-process-side">
                <div className="process-step">
                  <div className="process-num">01</div>
                  <div className="process-txt">
                    <h5>Technical Discovery</h5>
                    <p>Deep-dive into existing tech stacks and integration feasibility under NDA.</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="process-num">02</div>
                  <div className="process-txt">
                    <h5>Commercial Alignment</h5>
                    <p>Structuring the licensing or revenue model based on partnership depth.</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="process-num">03</div>
                  <div className="process-txt">
                    <h5>Scalable Onboarding</h5>
                    <p>Full technical handover, training, and deployment of partner instances.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="Partnership-cta-final">
        <div className="container">
          <div className="cta-inner">
            <h2 className="cta-h2">Ready to Forge a Partnership?</h2>
            <p className="cta-p">Join the Digital Dream Works ecosystem and transform how you deliver value to your clients.</p>
            <div className="cta-group">
              <a href="https://calendly.com/digi-dreamworks/onboarding-call" className="cta-btn-main">Schedule Strategy Call</a>
              <a href="mailto:hello@DigitalDreamWorksAgency.com" className="cta-btn-sub">Email Support</a>
            </div>
          </div>
        </div>
      </section>

      <div className="LD-divider"></div>
      <ChatbotWidget />
      <Call />
      <Footer />
    </div>
  );
}
